import type { Key } from './i18n'
import { codeOwner, isStorableCode, type PackCoded } from './barcodes'

/**
 * 180 (1.58) — set_product_pack's refusals, each in the owner's words. The
 * database is the one that says no (the price, the size, the product's kind,
 * the code); this only names the reason on the product dialog.
 */
const PACK_ERRORS: [string, Key][] = [
  ['PACK_PRICE_INVALID', 'store.packPrice'],
  ['PACK_QTY_INVALID', 'store.packQty'],
  ['PACK_PRODUCT_KIND', 'store.packKind'],
  ['PACK_CODE_TAKEN', 'store.packCodeTaken'],
  ['PACK_CODE_OWN', 'store.packCodeOwn'],
  ['BARCODE_INVALID', 'store.packCodeInvalid'],
  ['OWNER_ONLY', 'store.packOwnerOnly'],
]

export function packErrorKey(message: string): Key {
  return PACK_ERRORS.find(([code]) => message.includes(code))?.[1] ?? 'store.packFailed'
}

/**
 * Codex's final review I3 (181) — the box as the product dialog holds it, checked with
 * set_product_pack's own rules BEFORE anything is saved: a new product used to be created
 * first and its box refused after, the dialog stayed a „new product" form, and a second press
 * made a second product. The database still decides; this keeps the two saves from parting.
 */
export function packFormProblem(
  form: { qty: string; price: string; code: string },
  ctx: { sale: number; barcode: string; extras: readonly string[]; products: readonly (PackCoded & { id: string })[]; selfId: string | null },
): Key | null {
  if (form.qty.trim() === '') return null
  const qty = Number(form.qty.trim())
  if (!Number.isInteger(qty) || qty < 2 || qty > 10000) return 'store.packQty'
  const rawPrice = form.price.trim().replace(',', '.')
  if (rawPrice !== '') {
    const price = Number(rawPrice)
    if (!Number.isFinite(price) || price <= 0 || Math.abs(price * 100 - Math.round(price * 100)) > 1e-6 || price >= qty * ctx.sale - 0.0001) return 'store.packPrice'
  }
  const code = form.code.trim()
  if (code === '') return null
  if (!isStorableCode(code)) return 'store.packCodeInvalid'
  if (code === ctx.barcode.trim() || ctx.extras.some((c) => c.trim() === code)) return 'store.packCodeOwn'
  const taken = codeOwner(ctx.products, code, ctx.selfId)
    ?? ctx.products.find((p) => p.id !== ctx.selfId && (p.pack_barcode ?? '').trim() === code)
  return taken ? 'store.packCodeTaken' : null
}
